import { useEffect, useRef, useState } from 'react';

import { Button, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';

import PortfolioStakedCard from './card/PortfolioStakedCard';
import PortfolioDetailTable from './table/PortfolioDetailTable';
import ClaimModal from './modal/ClaimModal';
import BigNumber from 'bignumber.js';
import CommonButton from './CommonButton';
import {
  portfolioClaimRewardDoneColumns,
  portfolioPlanRewardDoneColumns,
  portfolioRewardDoneColumns,
  portfolioWidthrawColumns,
} from './table/ColumnsData';
import { ResUnStakingListType } from '@/types';
import { claimedList, expectList, rewardList, withdrawResultList } from '@/api/staking/read';
import { ClaimedListType, ContractInfoType, ListRewardListType, RewardListType } from '@/types/staking/read';
import { useWeb3 } from '@/hooks/use-web3';
import { now } from '@/lib/dayjs';
import { DEFAULT_DAY } from '@/lib/common/name-space';
import { packBigNumbers } from '@/lib/BigNumbers';
import { GridColDef } from '@mui/x-data-grid';
import PortfolioClaimedCard from './card/PortfolioClaimedCard';
import PortfolioWithdrawCard from './card/PortfolioWithdrawCard';
import PortfolioPlanCard from './card/PortfolioPlanCard';

export default function RewardList({ poolId, contractAddr }: { poolId: string; contractAddr: string }) {
  const [waitList, setWaitList] = useState<ListRewardListType[] | null>(null);
  const [claimList, setClaimList] = useState<ClaimedListType[] | null>(null);
  const [planList, setPlan] = useState<any | null>(null);
  const [isOpenClaimModal, setIsOpenClaimModal] = useState(false);
  const { web3authCall, address } = useWeb3();
  const isMobile = useMediaQuery('(max-width:900px)');
  const theme = useTheme();
  const claimInfo = useRef('');
  const [doneClaim, setDoneClaim] = useState(false); // 클레임 모달 이후 다시 랜더링
  const [contractInfo, setContractInfo] = useState<ContractInfoType>({
    contractAddr: contractAddr,
    index: -1, // 0번 사용 할 수 있어서 사용안하는 번호 입력
    stakeAmt: '',
    joinId: '',
    poolId,
    cnt: -1,
  });
  const [withdrawResult, setWithdrawResult] = useState<ResUnStakingListType | null>(null);
  useEffect(() => {
    if (doneClaim && web3authCall && poolId) {
      (async () => {
        setDoneClaim(false);
        await allList();
      })();
    }
    return () => {};
  }, [doneClaim]);

  useEffect(() => {
    if (web3authCall && poolId) {
      (async () => {
        await allList();
      })();
    }

    return () => {};
  }, [poolId, web3authCall]);

  const addPortfolioClaimRewardDoneColumns: GridColDef[] = [
    ...portfolioClaimRewardDoneColumns,
    {
      minWidth: 100,
      field: 'rewardHash',
      headerName: 'Tx',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button color="primary" onClick={() => handleButtonClick(params.value)}>
          ↗
        </Button>
      ),
    },
  ];
  const addPortfolioWithdrawColumns: GridColDef[] = [
    ...portfolioWidthrawColumns,
    {
      minWidth: 100,
      field: 'hash',
      headerName: 'Tx',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button color="primary" onClick={() => handleButtonClick(params.value)}>
          ↗
        </Button>
      ),
    },
  ];
  const allList = async () => {
    const obj: RewardListType = {
      poolId,
      address,
    };
    const [waitResult, claimResult, planResult, withdraw] = await Promise.allSettled([
      rewardList(obj),
      claimedList(obj),
      expectList(poolId),
      withdrawResultList(obj),
    ]);
    console.log(JSON.stringify(claimResult, null, 2));
    processList(waitResult, 'reward');
    processList(claimResult, 'claim');
    processPlanList(planResult);
    processWithdraw(withdraw);
  };

  const processList = (result: any, type: 'reward' | 'claim') => {
    if (result.status === 'fulfilled' && Object.keys(result.value.data).length > 0) {
      const re = result.value;
      if (re.data !== null) {
        const list = re.data.map((item: any, index: number) => ({
          ...item,
          no: index + 1,
          index: item.stakeIndex ?? index,
          totStakeAmt: packBigNumbers(BigNumber(item.totStakeAmt).toFixed(6, 1)),
          // newStartDt: now(item.newStartDt || item.startDt, DEFAULT_DAY),
          // newEndDt: now(item.newEndDt || item.endDt, DEFAULT_DAY),
          ...(type === 'reward' && {
            rewardAmt: packBigNumbers(BigNumber(item.stakingUserReward).toFixed(6, 1)),
          }),
          ...(type === 'claim' && {
            rewardAmt: packBigNumbers(BigNumber(item.rewardAmt).toFixed(6, 1)),
          }),
        }));

        if (type === 'reward') {
          setWaitList(list.reverse());
          // setList(list);
        } else if (type === 'claim') {
          setClaimList(list);
        }
      }
    }
    // else {
    //   console.error(`${type === 'reward' ? 'rewardList' : 'claimedList'} 요청 실패:`, result.reason);
    // }
  };
  const processPlanList = (result: any) => {
    if (result.status === 'fulfilled' && Object.keys(result.value.data).length > 0) {
      const re = result.value.data.map((item: any, index: number) => ({
        index,
        no: index + 1,
        startDt: now(item.startDt, DEFAULT_DAY),
        endDt: now(item.endDt, DEFAULT_DAY),
      }));
      setPlan(re);
    }
  };
  const processWithdraw = (result: any) => {
    if (result.status === 'fulfilled' && Object.keys(result.value.data).length > 0) {
      setWithdrawResult(result.value.data);
    }
  };

  const handleButtonClick = (aData: string) => {
    const src =
      process.env.NODE_ENV === 'development'
        ? `https://amoy.polygonscan.com/tx/${aData}`
        : `https://polygonscan.com/tx/${aData}`;
    window.open(src);
  };
  useEffect(() => {
    if (isOpenClaimModal) {
      updateContractInfo('contractAddr', contractAddr);
    }

    return () => {};
  }, [isOpenClaimModal]);

  const updateContractInfo = (key: string | Partial<ContractInfoType>, value?: string | number) => {
    if (typeof key === 'string' && value !== undefined) {
      setContractInfo((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (typeof key === 'object') {
      setContractInfo((prev) => ({
        ...prev,
        ...key,
      }));
    }
  };
  const handleOpenModal = (row: ListRewardListType) => {
    // 인덱스를 받아서 UnstakingModal넘겨주자
    claimInfo.current = row.joinId;
    console.log(row);
    updateContractInfo({
      index: row.index,
      stakeAmt: row.totStakeAmt,
      joinId: row.joinId,
      poolId: row.poolId,
      cnt: row.cnt,
    });

    setIsOpenClaimModal(true);
  };
  const addColumns: GridColDef[] = [
    ...portfolioRewardDoneColumns,
    // ※ 확장자 .ts에서는 renderCell을 불러올 수가 없어서 해당 기능만 추가
    {
      flex: 1,
      minWidth: 100,
      field: 'joinId',
      headerName: 'Status',
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <>
          <CommonButton
            sx={{ borderRadius: '5px', padding: '4px 20px', width: '80px' }}
            handleClick={() => {
              console.log(params);
              handleOpenModal(params.row);
            }}
            text="Claim"
            color="#ffffff"
            bgColor={'#357AFF'}
            variant="body2"
          />

          {/* <CommonButton
            sx={{ borderRadius: '5px', padding: '4px 20px', width: '80px' }}
            handleClick={() => {
              console.log(list?.length);

              console.log(params);
              handleOpenModal(params.row);
            }}
            text="Claim"
            // text={params.value === 'claim' ? 'Claim' : 'Unstake'}
            color="#ffffff"
            bgColor={'#357AFF'}
            variant="body2"
          /> */}
        </>
      ),
    },
  ];
  return (
    <>
      <Typography variant="h3" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
        Reward Detail
      </Typography>
      {isMobile ? (
        <>
          {waitList && waitList?.length > 0 && (
            <>
              <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                Waiting for a Claim
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {waitList.map((item: ListRewardListType, index: number) => {
                  // reverse된 배열에서 화면상 첫 번째 항목(item.index가 가장 큰 항목)만 활성화
                  // const maxIndex = Math.max(...waitList.map((i) => i.index));
                  // const isFirstItem = item.index === maxIndex;
                  return (
                    <PortfolioStakedCard key={item.index} item={item} handleOpenModal={() => handleOpenModal(item)} />
                  );
                })}
              </Box>
            </>
          )}
          {planList && planList?.length > 0 && (
            <>
              <Typography
                ml={2}
                variant="h4"
                color={theme.greyscale.darkgrey2}
                sx={{ marginBottom: '16px', margintop: '16px' }}
              >
                Claim to be reflected
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {planList.map((item: ListRewardListType) => (
                  <PortfolioPlanCard key={item.index} item={item} handleOpenModal={() => handleOpenModal(item)} />
                ))}
              </Box>
            </>
          )}
          {claimList && claimList?.length > 0 && (
            <>
              <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                Completion of Claim
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {claimList.map((item: ClaimedListType) => (
                  <PortfolioClaimedCard key={item.index} item={item} />
                ))}
              </Box>
            </>
          )}
          {withdrawResult && (
            <>
              <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                Withdrawal Detail
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <PortfolioWithdrawCard key={withdrawResult.joinId} item={withdrawResult} />
              </Box>
              * The amount of principal withdrawal SFL changes according to the SFL market price at the time of
              withdrawal, and <br /> when the SFL market price rises, the amount of principal withdrawal decreases, and
              when the SFL market price falls, the amount of principal withdrawal increases. <br />
              However, the amount converted to USDT is the same as the staking principal.
              <Box mb={2} />* SFL Principal : Reflects Average Market Price(USDT) at Pricipal withdrawal <br />*
              Cumulative Principal(USDT) : USDT total at each staking
            </>
          )}
        </>
      ) : (
        <>
          {waitList && waitList?.length > 0 && (
            <>
              <Grid container spacing={2}>
                <Grid xs={8}>
                  <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                    Waiting for a Claim
                  </Typography>
                </Grid>
              </Grid>
              {waitList && <PortfolioDetailTable list={waitList} columns={addColumns} />}
              <Box mb={6} />
            </>
          )}

          {planList && planList?.length > 0 && (
            <>
              <Grid container spacing={2}>
                <Grid xs={8}>
                  <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                    Claim to be reflected
                  </Typography>
                </Grid>
              </Grid>
              <PortfolioDetailTable list={planList} columns={portfolioPlanRewardDoneColumns} />
              <Box mb={6} />
            </>
          )}

          {claimList && claimList?.length > 0 && (
            <>
              <Grid container spacing={2}>
                <Grid xs={8}>
                  <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                    Completion of Claim
                  </Typography>
                </Grid>
              </Grid>
              {claimList && <PortfolioDetailTable list={claimList} columns={addPortfolioClaimRewardDoneColumns} />}
              <Box mb={6} />
            </>
          )}

          {withdrawResult && (
            <>
              <Grid container spacing={2}>
                <Grid xs={8}>
                  <Typography ml={2} variant="h4" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                    Withdrawal Detail
                  </Typography>
                </Grid>
              </Grid>
              {withdrawResult && <PortfolioDetailTable list={[withdrawResult]} columns={addPortfolioWithdrawColumns} />}
              * The amount of principal withdrawal SFL changes according to the SFL market price at the time of
              withdrawal, and <br /> when the SFL market price rises, the amount of principal withdrawal decreases, and
              when the SFL market price falls, the amount of principal withdrawal increases. <br />
              However, the amount converted to USDT is the same as the staking principal.
              <Box mb={2} />* SFL Principal : Reflects Average Market Price(USDT) at Pricipal withdrawal <br />
              *Cumulative Principal(USDT) : USDT total at each staking
            </>
          )}
        </>
      )}
      {contractInfo.contractAddr !== '' && contractInfo.index !== -1 && (
        <ClaimModal
          open={isOpenClaimModal}
          handleClose={() => setIsOpenClaimModal(false)}
          contractInfo={contractInfo}
          setDoneClaim={setDoneClaim}
        />
      )}
    </>
  );
}
